import { useState } from 'react';

import type { ListSource } from '../api/endpoints.ts';
import type { Category } from '../api/types.ts';
import { useStore } from '../core/store.ts';
import { errorText, useStrings } from '../i18n/index.ts';
import { homeStore, siblingsOf } from '../state/home.ts';
import { useProductList } from '../state/product-list.ts';
import { favoritesStore } from '../state/stores.ts';
import { Chip, EmptyState, ErrorBanner, ScreenTitle } from '../ui/kit.tsx';
import { ProductGrid, ProductGridSkeleton } from '../ui/ProductTile.tsx';
import { Screen } from '../ui/Screen.tsx';
import '../styles/storefront.css';

/**
 * Категория: сверху чипы соседних категорий раздела, ниже крупная сетка товаров.
 * Заголовок и содержимое меняются по чипу без нового экрана в стеке.
 */
export function CategoryScreen({ category }: { category: Category }) {
  const strings = useStrings();
  const categories = useStore(homeStore).categories;
  const [selected, setSelected] = useState(category);
  const siblings = siblingsOf(categories, selected);
  const list = useProductList({ kind: 'category', id: selected.id });

  return (
    <Screen
      pinned={
        siblings.length > 1 ? (
          <div className="lane category-chips">
            {siblings.map((sibling) => (
              <Chip key={sibling.id} title={sibling.name} selected={sibling.id === selected.id} onClick={() => setSelected(sibling)} />
            ))}
          </div>
        ) : undefined
      }
    >
      <div className="page stack" style={{ gap: 16 }}>
        <ScreenTitle text={selected.name} />
        {list.error ? (
          <div className="gutter">
            <ErrorBanner text={errorText(strings, list.error)} onRetry={list.reload} />
          </div>
        ) : list.isLoading ? (
          <ProductGridSkeleton />
        ) : list.items.length === 0 ? (
          <EmptyState icon="basket" title={strings.nothingFound} hint={strings.categoryEmptyHint} />
        ) : (
          <ProductGrid items={list.items} />
        )}
      </div>
    </Screen>
  );
}

/** Список товаров по источнику: подборка, скидки, новинки, избранное, покупки. */
export function ProductListScreen({ source, title }: { source: ListSource; title: string }) {
  const strings = useStrings();
  // Избранное перечитывается, когда сердце сняли прямо на этом экране.
  const favoritesCount = useStore(favoritesStore).size;
  const list = useProductList(source, source.kind === 'favorites' ? String(favoritesCount) : '');

  return (
    <Screen>
      <div className="page stack" style={{ gap: 16 }}>
        <ScreenTitle text={list.collection?.title ?? title} subtitle={list.collection?.subtitle} />
        {list.error ? (
          <div className="gutter">
            <ErrorBanner text={errorText(strings, list.error)} onRetry={list.reload} />
          </div>
        ) : list.isLoading ? (
          <ProductGridSkeleton />
        ) : list.items.length > 0 ? (
          <ProductGrid items={list.items} />
        ) : source.kind === 'favorites' ? (
          <EmptyState image="/empty/favorites.webp" title={strings.favoritesEmpty} hint={strings.favoritesEmptyHint} />
        ) : source.kind === 'purchases' ? (
          <EmptyState image="/empty/orders.webp" title={strings.noOrders} hint={strings.purchasesEmptyHint} />
        ) : (
          <EmptyState icon="basket" title={strings.nothingFound} hint={strings.categoryEmptyHint} />
        )}
      </div>
    </Screen>
  );
}
